import uniqueId from 'lodash/uniqueId';
import React, { FunctionComponent } from 'react';

import RichText from 'components/RichText/RichText';
import Text from 'components/Text/Text';
import Toast from 'components/Toast/Toast';
import useClipboard from 'hooks/useClipboard';
import { useCopyStore } from 'store';
import { appState } from 'utils/appState';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import { OfficeCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from './InfoContent.styles';

export interface InfoContentProps {
  addressLabel: string;
  addressLine: string;
  addressButton: OfficeCMS['addressButton'];
  contactLabel: string;
  telephoneNumber: string;
  emailFields: OfficeCMS['emailFields'];
  emails: OfficeCMS['emails'];
  contactText: string;
}

const defaultProps: Partial<InfoContentProps> = {
  addressLabel: '',
  addressLine: '',
  addressButton: {
    id: '1',
    label: '',
    href: '/',
    isExternal: true,
    socials: '',
  },
  contactLabel: '',
  telephoneNumber: '',
  emailFields: [],
  emails: [],
  contactText: '',
};

const InfoContent: FunctionComponent<InfoContentProps> = ({
  addressLabel,
  addressLine,
  addressButton,
  contactLabel,
  telephoneNumber,
  emailFields,
  emails,
  contactText,
  ...rest
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const { isCopied, copyToClipboard } = useClipboard();

  const hoverTextColor =
    globalSettings?.buttonsHoverColor ||
    globalSettings?.buttonsHoverDefaultColors;

  return (
    <S.Wrapper hoverTextColor={hoverTextColor} {...rest}>
      {addressLabel && (addressLine || addressButton) && (
        <address>
          <RichText
            as="p"
            size="heading4"
            richText={addressLabel}
            defaultFormatting
          />

          <S.Container>
            <S.TextRich richText={addressLine} />
            <Text
              as="a"
              size="small"
              text={addressButton?.label}
              title={addressButton?.label}
              href={addressButton?.href}
              target="_blank"
              rel="noreferrer"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          </S.Container>

          {addressButton?.socials && (
            <S.SocialsContainer>
              <RichText
                as="p"
                size="heading4"
                richText={addressButton?.socials}
                defaultFormatting
              />
            </S.SocialsContainer>
          )}
        </address>
      )}

      {contactLabel && (telephoneNumber || emailFields.length > 0) && (
        <S.GlobalContainer>
          <RichText
            as="p"
            size="heading4"
            richText={contactLabel}
            defaultFormatting
          />

          <S.Container>
            <Text
              as="a"
              size="small"
              href={`tel:${telephoneNumber}`}
              text={telephoneNumber}
            />

            <S.TextRich
              richText={contactText}
              $isMargin={!!contactText.length && !!telephoneNumber.length}
            />
            <S.Emails $isMargin={!!contactText.length}>
              {emailFields.map(({ title, email }) => (
                <Text
                  key={uniqueId()}
                  as="span"
                  size="small"
                  text={title}
                  onClick={() => {
                    copyToClipboard(email);
                    appState.analytics.trackEmailCopy('contact us');
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              ))}
            </S.Emails>
          </S.Container>
        </S.GlobalContainer>
      )}

      {emails &&
        emails.map(({ id, title, email }) => (
          <S.GlobalContainer key={id}>
            <Text size="heading4" text={title} />

            <S.Container>
              <Text
                as="span"
                size="small"
                text={email}
                onClick={() => {
                  copyToClipboard(email);
                  appState.analytics.trackEmailCopy('contact us');
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            </S.Container>
          </S.GlobalContainer>
        ))}
      {isCopied && <Toast text={globalSettings?.copiedText || '✓'} />}
    </S.Wrapper>
  );
};

InfoContent.defaultProps = defaultProps;

export default InfoContent;
